/**
 * announcementAudience — who an admin's urgent notice is for.
 *
 * ONE definition of the semantics, LITERALLY shared: this module lives in
 * @muaa/shared because the mobile app EVALUATES the audience and the admin
 * DESCRIBES it, and a summary sentence that disagrees with the predicate is the
 * worst possible bug here — it tells an admin they are reaching people they are
 * not. Both workspaces import `describeAudience` from this one file. The audience
 * lives
 * in `app_config.announcement_audience`, a jsonb OBJECT where `{}` means
 * EVERYONE. Every recognised key is optional and they are ANDed together.
 *
 *   { "platforms": ["ios"] }                    → iPhone/iPad only
 *   { "platforms": ["android"] }                → Android only
 *   { "signedIn": false }                       → only people who are NOT signed in
 *   { "minAppVersion": "1.2.0" }                → 1.2.0 and newer
 *   { "maxAppVersion": "1.1.9" }                → "you are on an old build, update"
 *   { "platforms": ["android"], "maxAppVersion": "1.0.4" }  → both must hold
 *
 * THREE RULES, ALL DELIBERATE:
 *
 * 1. UNKNOWN KEYS ARE IGNORED, never treated as a non-match. A bundle that
 *    predates a future targeting dimension still shows the notice. The opposite
 *    (unknown key ⇒ no match) would mean shipping a new dimension silently
 *    blanks the banner on every older install — the exact failure an urgent
 *    notice cannot have.
 *
 * 2. A MALFORMED VALUE IS IGNORED, not fatal. `platforms: "ios"` (a string, not
 *    an array) or `minAppVersion: 3` is treated as "no constraint on that axis"
 *    rather than throwing. The row is admin-authored free-form jsonb with no
 *    per-key CHECK behind it, so this function is the only thing between a
 *    fat-fingered write and a crash on every device at once.
 *
 * 3. ONLY SESSION-LESS FACTS ARE TARGETABLE. Platform, app version and
 *    signed-in-ness are all knowable before login, which is where an
 *    access-related notice matters most. Member attributes (class year, honor
 *    year, admin-ness) are deliberately absent: a notice targeted on them could
 *    not be evaluated by the audience this feature exists to reach.
 *
 * Pure, dependency-free and DOM/RN-free on purpose — it unit-tests with zero
 * mocks and imports cleanly into a Vite browser bundle and a Metro one alike.
 *
 * TESTS LIVE AT mobile/src/lib/__tests__/announcementAudience.test.ts. The shared
 * workspace has no test runner (`npm run test:shared` is a typecheck only), and
 * node_modules/@muaa/shared is a symlink to this directory, so mobile's jest
 * exercises this exact source.
 */

/** What a session-less client knows about itself. */
export interface AudienceContext {
  /** `Platform.OS` — only 'ios' and 'android' ship today. */
  platform: string;
  /** `Constants.expoConfig?.version`, e.g. '1.0.0'. Undefined when unreadable. */
  appVersion?: string;
  /** Whether a session is currently restored. */
  signedIn: boolean;
}

/** The recognised shape of `app_config.announcement_audience`. All optional. */
export interface AnnouncementAudience {
  platforms?: string[];
  signedIn?: boolean;
  minAppVersion?: string;
  maxAppVersion?: string;
}

/**
 * Compare dotted-numeric versions. Returns <0, 0, >0 like a comparator.
 *
 * Deliberately NOT semver: this compares `app.json`'s `version`, which this
 * project keeps as plain `major.minor.patch`. Missing segments count as 0, so
 * '1.2' === '1.2.0'. Any segment that is not a number makes the whole comparison
 * unusable — the caller treats that as "no constraint" rather than guessing.
 */
export function compareVersions(a: string, b: string): number | null {
  const parse = (v: string): number[] | null => {
    const parts = v.trim().split('.');
    const nums: number[] = [];
    for (const p of parts) {
      if (!/^\d+$/.test(p)) return null;
      nums.push(Number(p));
    }
    return nums.length ? nums : null;
  };
  const av = parse(a);
  const bv = parse(b);
  if (!av || !bv) return null;
  const len = Math.max(av.length, bv.length);
  for (let i = 0; i < len; i++) {
    const d = (av[i] ?? 0) - (bv[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** True when `value` is a usable non-empty array of strings. */
function stringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const out = value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  return out.length ? out : null;
}

/**
 * Does this device fall inside the audience?
 *
 * A missing, non-object or empty audience matches EVERYONE — that is the
 * default state of the column and the answer an admin gets by not choosing.
 */
export function matchesAudience(
  audience: unknown,
  ctx: AudienceContext,
): boolean {
  if (audience == null || typeof audience !== 'object' || Array.isArray(audience)) {
    return true;
  }
  const a = audience as Record<string, unknown>;

  const platforms = stringArray(a.platforms);
  if (platforms && !platforms.some((p) => p.toLowerCase() === ctx.platform.toLowerCase())) {
    return false;
  }

  if (typeof a.signedIn === 'boolean' && a.signedIn !== ctx.signedIn) {
    return false;
  }

  // Version bounds are INCLUSIVE. An unreadable device version, or a bound that
  // is not dotted-numeric, drops the constraint rather than hiding the notice —
  // rule 2 above.
  if (typeof a.minAppVersion === 'string' && ctx.appVersion) {
    const cmp = compareVersions(ctx.appVersion, a.minAppVersion);
    if (cmp !== null && cmp < 0) return false;
  }
  if (typeof a.maxAppVersion === 'string' && ctx.appVersion) {
    const cmp = compareVersions(ctx.appVersion, a.maxAppVersion);
    if (cmp !== null && cmp > 0) return false;
  }

  return true;
}

/**
 * A short human sentence for an audience — used by the admin preview so whoever
 * publishes can read back who they are about to interrupt.
 *
 * Lives here rather than in the admin so the wording and the matcher stay in one
 * file: a phrase that disagrees with the predicate is worse than no phrase.
 */
export function describeAudience(audience: unknown): string {
  if (audience == null || typeof audience !== 'object' || Array.isArray(audience)) {
    return 'Everyone';
  }
  const a = audience as Record<string, unknown>;
  const parts: string[] = [];

  const platforms = stringArray(a.platforms);
  if (platforms) {
    const label = platforms
      .map((p) => (p.toLowerCase() === 'ios' ? 'iPhone' : p.toLowerCase() === 'android' ? 'Android' : p))
      .join(' and ');
    parts.push(`${label} users`);
  }
  if (typeof a.signedIn === 'boolean') {
    parts.push(a.signedIn ? 'signed-in members' : 'people who are not signed in');
  }
  // Only describe a bound the MATCHER would actually apply. compareVersions
  // returns null for anything that is not dotted-numeric, and matchesAudience
  // drops such a bound as "no constraint" — so describing it anyway told the
  // publishing admin their notice was NARROWER than it really was. That is the
  // precise failure this module exists to prevent (see the header), and it was
  // the one input class on which the two halves disagreed. Parsing via the same
  // function the matcher uses is what keeps them in step.
  const usable = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    if (!t) return null;
    return compareVersions(t, t) === null ? null : t;
  };

  const min = usable(a.minAppVersion);
  if (min) parts.push(`app ${min} or newer`);
  const max = usable(a.maxAppVersion);
  if (max) parts.push(`app ${max} or older`);

  if (!parts.length) return 'Everyone';
  return parts.join(', ');
}
